import { X } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { ReactNode } from 'react';

interface BaseActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  maxHeight?: string;
  headerContent?: ReactNode;
  footer?: ReactNode;
}

export function BaseActionSheet({
  isOpen,
  onClose,
  title,
  children,
  maxHeight = '75vh',
  headerContent,
  footer,
}: BaseActionSheetProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />

          {/* Sheet */}
          <motion.div
            className="fixed bottom-0 left-0 right-0 bg-white z-50 max-w-md mx-auto flex flex-col"
            style={{
              borderTopLeftRadius: 'var(--radius-card)',
              borderTopRightRadius: 'var(--radius-card)',
              maxHeight,
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
              <div
                className="rounded-full"
                style={{
                  width: '40px',
                  height: '4px',
                  backgroundColor: 'var(--muted)',
                }}
              />
            </div>

            {/* Header */}
            <div
              className="px-5 pb-4 pt-2 flex-shrink-0"
              style={{
                borderBottom: '1px solid var(--border)',
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <h3
                  className="text-foreground"
                  style={{
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-weight-semibold)',
                    lineHeight: '1.4',
                  }}
                >
                  {title}
                </h3>
                <button
                  onClick={onClose}
                  className="flex items-center justify-center transition-colors"
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: 'var(--radius-button)',
                    borderWidth: 0,
                    backgroundColor: 'transparent',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--muted)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  aria-label="Fechar"
                >
                  <X
                    className="w-5 h-5"
                    style={{ color: 'var(--muted-foreground)' }}
                  />
                </button>
              </div>

              {/* Header Content - rendered below title but above divider */}
              {headerContent && <div>{headerContent}</div>}
            </div>

            {/* Content */}
            <div
              className="flex-1 overflow-y-auto"
              style={{
                paddingTop: '16px',
                paddingBottom: '16px',
                scrollBehavior: 'smooth',
                // Custom scrollbar styling
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--muted) transparent',
                // Webkit scrollbar for Chrome/Safari
                WebkitOverflowScrolling: 'touch',
              }}
              // Inline style for webkit scrollbar (using dangerouslySetInnerHTML alternative)
              onMouseEnter={(e) => {
                const style = document.createElement('style');
                style.textContent = `
                  .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                  }
                  .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                  }
                  .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: var(--muted);
                    border-radius: 3px;
                  }
                  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: var(--muted-foreground);
                  }
                `;
                if (!document.getElementById('custom-scrollbar-styles')) {
                  style.id = 'custom-scrollbar-styles';
                  document.head.appendChild(style);
                }
                e.currentTarget.classList.add('custom-scrollbar');
              }}
            >
              {children}
            </div>

            {/* Footer */}
            {footer && (
              <div
                className="px-5 py-4 flex-shrink-0"
                style={{
                  borderTop: '1px solid var(--border)',
                  boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.04)',
                }}
              >
                {footer}
              </div>
            )}

            {/* Safe area padding */}
            <div className="h-6 flex-shrink-0" />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
